/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */
import _ from 'lodash';
import React from 'react';

import { Grid, Form, Divider } from '@mairistem/composants';

import type { Individu } from '@/types';

import InformationIndividu from '../InformationIndividu';

interface FiliationPanelProps {
  identifiant: number | string;
  parent1: Partial<Individu>;
  parent2: Partial<Individu>;
  recense: Partial<Individu>;
  updated: boolean;
  dateNaissanceRecense?: {
    anneeNaissance: number;
    moisNaissance: number;
    jourNaissance: number;
  };
  onChange: (data: Partial<Individu>) => void;
  onSave: () => Promise<void>;
  onRollback: () => void;
}

const FiliationPanel = (props: FiliationPanelProps) => {
  const {
    identifiant,
    parent1: parent1Props,
    parent2: parent2Props,
    recense,
    updated,
    dateNaissanceRecense,
    onChange,
    onSave,
    onRollback,
  } = props;

  const [parent1, setParent1] = React.useState<Partial<Individu>>(parent1Props);
  const [parent2, setParent2] = React.useState<Partial<Individu>>(parent2Props);
  const [isUnknown1, setIsUnknown1] = React.useState<boolean>(false);
  const [isUnknown2, setIsUnknown2] = React.useState<boolean>(false);

  const handleChangeParent1 = React.useCallback((data) => {
    setParent1((prev: Individu) => ({ ...prev, ...data }));
  }, []);

  React.useEffect(() => {
    if (!_.isEmpty(parent1Props)) {
      setParent1(parent1Props);
    }
  }, [parent1Props]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: need
  React.useEffect(() => {
    if (!_.isNil(parent1) && !_.isEqual(parent1, parent1Props)) {
      onChange({
        parent1,
        filiationInconnueParent1: isUnknown1,
      });
    }
  }, [parent1, isUnknown1]);
  // Fix bug parent switch individu
  // }, [/*parent1, parent2Props, onChange,*/ isUnknown1]);

  const handleChangeParent2 = React.useCallback((data) => {
    setParent2((prev: Individu) => ({ ...prev, ...data }));
  }, []);

  React.useEffect(() => {
    if (!_.isEmpty(parent2Props)) {
      setParent2(parent2Props);
    }
  }, [parent2Props]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: need
  React.useEffect(() => {
    if (!_.isNil(parent2) && !_.isEqual(parent2, parent2Props)) {
      onChange({
        parent2,
        filiationInconnueParent2: isUnknown2,
      });
    }
  }, [parent2, isUnknown2]);
  // Fix bug parent switch individu
  // }, [/*parent2, parent2Props, onChange,*/ isUnknown2]);

  const handleFiliationInconnue = React.useCallback(
    (unknown: boolean, parentNumber: number) => {
      if (parentNumber === 1) {
        setIsUnknown1(unknown);
        setParent1((prev: Individu) => {
          if (unknown) {
            return {
              filiationInconnueParent1: unknown,
            };
          }
          return { ...prev, filiationInconnueParent1: unknown };
        });
      } else if (parentNumber === 2) {
        setIsUnknown2(unknown);
        setParent2((prev: Individu) => {
          if (unknown) {
            return { filiationInconnueParent2: unknown };
          }
          return { ...prev, filiationInconnueParent2: unknown };
        });
      }
    },
    [],
  );

  return (
    <Grid as={Form}>
      <InformationIndividu
        {...parent1}
        identifiant={identifiant}
        data-testid="panel-edit-modal-parent1"
        parent
        title={
          _.isNil(parent1Props) ? 'PARENT 1 - Filiation inconnue' : 'PARENT 1'
        }
        titleModal="Informations d'état civil et de de naissance du parent 1"
        recense={recense?.parent1}
        updated={updated}
        filiationInconnue={recense?.filiationInconnueParent1}
        dateNaissanceRecense={dateNaissanceRecense}
        onFiliationInconnueChange={(isUnknown) =>
          handleFiliationInconnue(isUnknown, 1)
        }
        onChange={handleChangeParent1}
        onSave={onSave}
        onRollback={onRollback}
      />
      <Divider />
      <InformationIndividu
        {...parent2}
        identifiant={identifiant}
        data-testid="panel-edit-modal-parent2"
        parent
        title={
          _.isNil(parent2Props) ? 'PARENT 2 - Filiation inconnue' : 'PARENT 2'
        }
        titleModal="Informations d'état civil et de de naissance du parent 2"
        recense={recense?.parent2}
        updated={updated}
        filiationInconnue={recense?.filiationInconnueParent2}
        dateNaissanceRecense={dateNaissanceRecense}
        onFiliationInconnueChange={(isUnknown) =>
          handleFiliationInconnue(isUnknown, 2)
        }
        onChange={handleChangeParent2}
        onSave={onSave}
        onRollback={onRollback}
      />
    </Grid>
  );
};

export default FiliationPanel;
